// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

/**
 * For of original react-router useScrollRestoration implementation with added support for custom scrollable element
 * https://github.com/remix-run/react-router/blob/132c160633fac43e970d894647bcd917333c59ce/packages/react-router/lib/dom/lib.tsx#L3014
 * TODO: remove when react-router adds support for custom scrollable element (https://github.com/remix-run/react-router/discussions/9495)
*/
import { useCallback, useContext, useEffect, useLayoutEffect } from 'react';
import {
    GetScrollRestorationKeyFunction,
    UNSAFE_DataRouterContext as DataRouterContext,
    UNSAFE_DataRouterStateContext as DataRouterStateContext,
    useLocation,
    useMatches,
    useNavigation,
    useBeforeUnload,
} from 'react-router';

const SCROLL_RESTORATION_STORAGE_KEY = 'react-router-scroll-positions';
let savedScrollPositions: Record<string, number> = {};

const useDataRouterContext = () => {
    const ctx = useContext(DataRouterContext);
    if (!ctx) {
        throw new Error('useDataRouterContext must be used within a DataRouterContext provider');
    }
    return ctx;
};

const useDataRouterState = () => {
    const state = useContext(DataRouterStateContext);
    if (!state) {
        throw new Error('useDataRouterState must be used within a DataRouterStateContext provider');
    }
    return state;
};


export const useScrollRestoration = ({
    getKey,
    storageKey,
    elementRef,
}: {
    getKey?: GetScrollRestorationKeyFunction;
    storageKey?: string;
    elementRef?: React.RefObject<HTMLElement>;
} = {}) => {
    const { router } = useDataRouterContext();
    const { restoreScrollPosition, preventScrollReset } = useDataRouterState();
    const location = useLocation();
    const matches = useMatches();
    const navigation = useNavigation();

    // Trigger manual scroll restoration while we're active
    useEffect(() => {
        window.history.scrollRestoration = 'manual';
        return () => {
            window.history.scrollRestoration = 'auto';
        };
    }, []);

    // Save positions on unload
    useBeforeUnload(
        useCallback(() => {
            if (navigation.state === 'idle') {
                const key = (getKey ? getKey(location, matches) : null) || location.key;
                const scrollValue = elementRef?.current?.scrollTop ?? window.scrollY;
                savedScrollPositions[key] = scrollValue;
            }
            sessionStorage.setItem(
                storageKey || SCROLL_RESTORATION_STORAGE_KEY,
                JSON.stringify(savedScrollPositions),
            );
            window.history.scrollRestoration = 'auto';
        }, [storageKey, getKey, navigation.state, location, matches, elementRef]),
    );

    // Read in any saved scroll locations
    useLayoutEffect(() => {
        try {
            const sessionPositions = sessionStorage.getItem(
                storageKey || SCROLL_RESTORATION_STORAGE_KEY,
            );
            if (sessionPositions) {
                savedScrollPositions = JSON.parse(sessionPositions);
            }
        } catch {
            // no-op, use default empty object
        }
    }, [storageKey]);

    // Enable scroll restoration in the router
    useLayoutEffect(() => {
        const disableScrollRestoration = router?.enableScrollRestoration(
            savedScrollPositions,
            () => {
                const value = elementRef?.current?.scrollTop ?? window.scrollY;
                return value;
            },
            getKey,
        );
        return () => disableScrollRestoration && disableScrollRestoration();
    }, [router, getKey, elementRef]);

    // Restore scrolling when state.restoreScrollPosition changes
    useLayoutEffect(() => {
        // Explicit false means don't do anything (used for submissions)
        if (restoreScrollPosition === false) {
            return;
        }

        // been here before, scroll to it
        if (typeof restoreScrollPosition === 'number') {
            if (elementRef?.current) {
                elementRef.current.scroll(0, restoreScrollPosition);
                return;
            }
            window.scrollTo(0, restoreScrollPosition);
            return;
        }

        // try to scroll to the hash
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                el.scrollIntoView();
                return;
            }
        }

        // Opt out of scroll reset if this link requested it
        if (preventScrollReset === true) {
            return;
        }

        // otherwise go to the top on new locations
        if (elementRef?.current) {
            elementRef.current.scroll(0, 0);
            return;
        }
        window.scrollTo(0, 0);
    }, [location, restoreScrollPosition, preventScrollReset, elementRef]);
};
